<template>
    <v-dialog :model-value="modelValue" max-width="560" @update:model-value="close">
        <v-card rounded="lg">
            <v-card-title class="d-flex align-center">
                <span class="text-h6">Enroll assets</span>
                <v-spacer/>
                <v-btn icon="mdi-close" variant="text" size="small" @click="close"/>
            </v-card-title>

            <v-card-text>
                <template v-if="loading">
                    <v-skeleton-loader type="list-item-two-line@4"/>
                </template>
                <template v-else-if="capable.length === 0">
                    <div class="py-6 text-center text-medium-emphasis">
                        {{ emptyMessage }}
                    </div>
                </template>
                <template v-else>
                    <v-text-field v-model="search" density="compact" variant="outlined" hide-details
                                  prepend-inner-icon="mdi-magnify" placeholder="Search" class="mb-2"/>
                    <div class="d-flex align-center justify-space-between mb-1">
                        <v-checkbox-btn :model-value="allSelected" :indeterminate="someSelected"
                                        label="Select all" @update:model-value="toggleAll"/>
                        <span class="text-caption text-medium-emphasis">{{ selected.length }} selected</span>
                    </div>
                    <v-divider/>
                    <div class="enroll-list">
                        <v-list density="comfortable">
                            <v-list-item v-for="asset in visible" :key="asset.id"
                                         :disabled="!asset.carb_capable" @click="toggle(asset.id)">
                                <template #prepend>
                                    <v-checkbox-btn :model-value="selected.includes(asset.id)"
                                                    :disabled="!asset.carb_capable" @click.stop="toggle(asset.id)"/>
                                </template>
                                <v-list-item-title>{{ asset.label || ('Asset ' + asset.id) }}</v-list-item-title>
                                <v-list-item-subtitle v-if="!asset.carb_capable" class="text-warning">
                                    Device is not CARB-capable
                                </v-list-item-subtitle>
                            </v-list-item>
                        </v-list>
                    </div>
                </template>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
                <v-spacer/>
                <v-btn variant="text" @click="close">Cancel</v-btn>
                <v-btn color="primary" :disabled="selected.length === 0" :loading="saving" @click="submit">
                    Enroll {{ selected.length || '' }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {computed, ref, watch} from 'vue';
import {send} from '@/composables/apiClient';
import {useSnackbarStore} from '@/stores/snackBarStore';

const props = defineProps({modelValue: {type: Boolean, default: false}});
const emit = defineEmits(['update:modelValue', 'enrolled']);

const {showSnackbar} = useSnackbarStore();

const loading = ref(false);
const saving = ref(false);
const assets = ref([]);
const selected = ref([]);
const search = ref('');

// Un-enrolled assets feed the list; non-capable ones render disabled.
const enrollable = computed(() => assets.value.filter(a => !a.enrolled));

// True when the company owns at least one CARB-capable asset, regardless
// of enrollment state. Drives which empty message to show.
const hasAnyCapable = computed(() => assets.value.some(a => a.carb_capable));

const emptyMessage = computed(() => hasAnyCapable.value
    ? 'All CARB-capable assets are already enrolled.'
    : 'None of your assets have a CARB-capable device.');

const visible = computed(() => {
    const q = search.value.trim().toLowerCase();
    if (!q) return enrollable.value;
    return enrollable.value.filter(a => (a.label || '').toLowerCase().includes(q));
});

// Only CARB-capable assets can actually be enrolled; select-all and the
// counters operate on those, never the disabled non-capable rows.
const capable = computed(() => enrollable.value.filter(a => a.carb_capable));

const allSelected = computed(() => capable.value.length > 0 && selected.value.length === capable.value.length);
const someSelected = computed(() => selected.value.length > 0 && !allSelected.value);

const toggle = (id) => {
    const asset = enrollable.value.find(a => a.id === id);
    if (!asset || !asset.carb_capable) return;
    const i = selected.value.indexOf(id);
    if (i === -1) selected.value.push(id); else selected.value.splice(i, 1);
};

const toggleAll = () => {
    selected.value = allSelected.value ? [] : capable.value.map(a => a.id);
};

const close = () => emit('update:modelValue', false);

const load = () => {
    loading.value = true;
    selected.value = [];
    search.value = '';
    send('get', 'ctc/enrollable-assets')
        .then((res) => {
            // Keep the full set so the empty state can tell "none capable"
            // apart from "all capable already enrolled". The list itself only
            // shows un-enrolled rows, with non-capable ones disabled.
            assets.value = res.data.data || [];
        }).catch(() => showSnackbar('Assets could not be loaded', 'error'))
        .finally(() => loading.value = false);
};

const submit = () => {
    saving.value = true;
    send('post', 'ctc/enrollments/bulk', {asset_ids: selected.value})
        .then((res) => {
            if (res.data.success === false) {
                showSnackbar(res.data.message || 'Enrollment failed', 'error');
                return;
            }
            const result = res.data.data || {};
            const notCapable = result.not_capable || [];
            if (notCapable.length) {
                showSnackbar(
                    `${result.enrolled} enrolled. ${notCapable.length} device(s) are not CARB-capable and cannot collect scans.`,
                    'warning',
                );
            } else {
                showSnackbar(`${result.enrolled ?? selected.value.length} asset(s) enrolled`);
            }
            emit('enrolled', [...selected.value]);
            close();
        })
        .catch((err) => {
            const msg = err?.response?.status === 403
                ? 'You do not have permission to enroll assets'
                : 'Enrollment failed';
            showSnackbar(msg, 'error');
        })
        .finally(() => saving.value = false);
};

watch(() => props.modelValue, (open) => {
    if (open) load();
});
</script>

<style scoped>
.enroll-list {
    max-height: 320px;
    overflow-y: auto;
}
</style>
